import { createSlice } from '@reduxjs/toolkit';
import {
	fetchCompanyById,
	updateEmployeeExcludedFlag,
	updateJournalSummarizedViewFlag,
	updateTimeHoursDecimalFlag,
	updateJeAuditSuffixSetting,
} from 'redux/action/companyAction';
import { updateConfiguration } from 'redux/action/configurationAction';
import { toastText } from 'utils/utils';

const initialState: any = {
	data: null,
	selectedCompanyDetails: null,
	configurations: null,
	isLoading: false,
	updateConfigurationLoader: false,
	error: null,
	currentCompany: null,
};

const CompanySlice = createSlice({
	name: 'company',
	initialState,
	reducers: {
		getCompanies: (state, data) => {
			const companies = data?.payload?.data?.companies;
			const companyId = localStorage.getItem('companyId');
			const isStoredIdValid =
				!!companyId && companyId !== 'undefined' && companyId !== 'null';

			// ENG-407 (R4): bootstrap and mid-session-race are distinct cases —
			//  1. No stored companyId (first login / cleared session): seed it from the
			//     first company in the list (companies[0]), exactly as the app has always
			//     done. This bootstrap is intentionally preserved. Note localStorage can
			//     hold the sentinel strings "undefined" or "null" (a prior setItem of a
			//     missing value) — those are truthy but not a real id, so isStoredIdValid
			//     treats them as no stored id and bootstraps normally.
			//  2. A companyId IS stored and IS found as a connected company: no write.
			//  3. A companyId IS stored but is NOT found as a connected company: do NOT
			//     overwrite it. The prior code reassigned this to companies[0] (with no
			//     isConnected check), which could flip the active company out from under
			//     an open screen mid-session — e.g. saving a mapping template under a
			//     different company than the Load dropdown is fetching for. Leave
			//     localStorage untouched and let the normal auth/redirect flow surface a
			//     genuinely invalid stored id. Explicit switches go through CompanySelector.
			if (!isStoredIdValid && companies?.length > 0) {
				const connectedCompany = companies[0];

				localStorage.setItem('companyId', connectedCompany?.company?.id);
				localStorage.setItem(
					'companyName',
					connectedCompany?.company?.tenantName
				);
			}
			state.data = companies;
			const companyData = companies?.find(
				(singleCompany: any) =>
					singleCompany?.company?.id === localStorage.getItem('companyId') &&
					singleCompany?.company?.isConnected
			);
			state.selectedCompanyDetails = companyData;
			state.configurations = companyData?.company?.configuration;
			state.currentCompany = companyData?.company;
		},
		getCompanyDetails: (state, data) => {
			const companyDetails = state?.data?.filter(
				(company: any) => company?.companyId === data?.payload
			);
			state.selectedCompanyDetails = companyDetails[0];
			state.configurations = companyDetails[0]?.company?.configuration;
			state.currentCompany = companyDetails[0]?.company;
		},

		updateCompanyStatus: (state, data) => {
			const companyDetails = state?.data?.find(
				(company: any) => company?.companyId == data?.payload?.data?.key
			);

			const companyUpdatedDetails = {
				...companyDetails,
				company: {
					...companyDetails?.company,
					status: data?.payload?.value,
				},
			};

			state.selectedCompanyDetails = companyUpdatedDetails;

			state.data = state?.data?.map((company: any) => {
				if (company?.companyId == data?.payload?.data?.key) {
					return companyUpdatedDetails;
				} else {
					return company;
				}
			});
		},

		updateCompanyConnect: (state, data) => {
			const companyDetails = state?.data?.find(
				(company: any) => company?.companyId == data?.payload?.companyId
			);

			const companyUpdatedDetails = {
				...companyDetails,
				company: {
					...companyDetails?.company,
					isConnected: data?.payload?.value,
				},
			};

			state.selectedCompanyDetails = companyUpdatedDetails;

			state.data = state?.data?.map((company: any) => {
				if (company?.companyId == data?.payload?.companyId) {
					return companyUpdatedDetails;
				} else {
					return company;
				}
			});
		},

		timeActivitySyncDate: (state) => {
			state.selectedCompanyDetails.company.timeActivitiesLastSyncDate =
				new Date();
		},

		employeeCostSyncDate: (state) => {
			state.selectedCompanyDetails.company.employeeLastSyncDate = new Date();
		},
	},
	extraReducers: (builder) => {
		builder.addCase(updateConfiguration.pending, (state) => {
			state.updateConfigurationLoader = true;
		});
		builder.addCase(updateConfiguration.fulfilled, (state, action) => {
			state.updateConfigurationLoader = false;
			state.configurations = action.payload.data;
			toastText(action.payload.message, 'success');
		});
		builder.addCase(updateConfiguration.rejected, (state, action: any) => {
			state.updateConfigurationLoader = false;
			toastText(action.payload.message, 'error');
		});

		// builder.addCase(updateEmployeeExcludedFlag.fulfilled, (state, action) => {
		// 	const updatedCompany = action.payload;

		// 	// Update selectedCompanyDetails if it matches
		// 	if (state.selectedCompanyDetails?.company?.id === updatedCompany.id) {
		// 		state.selectedCompanyDetails = {
		// 			...state.selectedCompanyDetails,
		// 			company: {
		// 				...state.selectedCompanyDetails.company,
		// 				isEmployeeExcludedInJournal:
		// 					updatedCompany.isEmployeeExcludedInJournal,
		// 			},
		// 		};
		// 	}

		// 	// Update in data array if present
		// 	state.data = state.data?.map((company: any) => {
		// 		if (company.company?.id === updatedCompany.id) {
		// 			return {
		// 				...company,
		// 				company: {
		// 					...company.company,
		// 					isEmployeeExcludedInJournal:
		// 						updatedCompany.isEmployeeExcludedInJournal,
		// 				},
		// 			};
		// 		}
		// 		return company;
		// 	});
		// });

		builder.addCase(updateEmployeeExcludedFlag.fulfilled, (state, action) => {
			const updatedCompany = action.payload;

			if (state.currentCompany?.id === updatedCompany.id) {
				state.currentCompany = {
					...state.currentCompany,
					isEmployeeExcludedInJournal:
						updatedCompany.isEmployeeExcludedInJournal,
				};
			}
		});

		builder.addCase(
			updateJournalSummarizedViewFlag.fulfilled,
			(state, action) => {
				const updatedCompany = action.payload;

				if (state.currentCompany?.id === updatedCompany.id) {
					state.currentCompany = {
						...state.currentCompany,
						isSummarizedJournalView: updatedCompany.isSummarizedJournalView,
					};
				}
			}
		);

		builder.addCase(updateJeAuditSuffixSetting.fulfilled, (state, action) => {
			const updatedCompany = action.payload;
			if (updatedCompany && state.currentCompany?.id === updatedCompany.id) {
				state.currentCompany = {
					...state.currentCompany,
					jeAuditSuffixEnabled: updatedCompany.jeAuditSuffixEnabled,
					jeAuditSuffix: updatedCompany.jeAuditSuffix ?? null,
				};
			}
		});

		builder.addCase(
			updateTimeHoursDecimalFlag.fulfilled,
			(state, action) => {
				const updatedCompany = action.payload;

				if (state.currentCompany?.id === updatedCompany.id) {
					state.currentCompany = {
						...state.currentCompany,
						isTimeHoursDecimal: updatedCompany.isTimeHoursDecimal,
					};
				}
			}
		);

		builder.addCase(fetchCompanyById.pending, (state) => {
			state.isLoading = true;
		});

		builder.addCase(fetchCompanyById.fulfilled, (state, action) => {
			state.isLoading = false;

			const fetchedCompany = action.payload;

			// Store in new state field
			state.currentCompany = fetchedCompany;

			// Optionally update in `data` list if it exists
			const index = state.data?.findIndex(
				(c: any) => c.company.id === fetchedCompany.id
			);
			if (index !== -1) {
				state.data[index].company = fetchedCompany;
			}
		});

		builder.addCase(fetchCompanyById.rejected, (state, action: any) => {
			state.isLoading = false;
			state.error =
				action.payload?.message || 'Failed to fetch company details';
		});
	},
});

export const {
	getCompanies,
	getCompanyDetails,
	updateCompanyStatus,
	updateCompanyConnect,
	timeActivitySyncDate,
	employeeCostSyncDate,
} = CompanySlice.actions;
export default CompanySlice;
